<template>
  <!-- Added min-h-0 so nested flex children can shrink and allow internal scrolling -->
  <div class="h-full flex flex-col min-h-0">
    <!-- Session selector --------------------------------------------------->
    <div class="flex-none p-2">
      <div class="flex items-center gap-2 ps-6">
        <div
          class="relative inline-flex items-center max-w-[200px] focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-200 focus-within:ring-offset-2 focus-within:ring-offset-white rounded"
        >
          <!-- Native selects don't reliably support gradient text; render the visual label separately -->
          <span
            class="block truncate pr-10 text-md font-semibold leading-[1.41] bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-indigo-500 to-purple-500"
          >
            {{ selectedSession ? formatOpt(selectedSession) : 'New AI Chat' }}
          </span>
          <select
            v-model="selectedId"
            @change="onSelect"
            class="absolute inset-0 m-w-[200px] w-full h-full opacity-0 px-2 py-1 cursor-pointer text-gray-900 bg-white dark_bg-gray-900 dark_text-gray-300"
          >
            <option v-for="s in sessions" :key="s.id" :value="s.id">
              {{ formatOpt(s) }}
            </option>
          </select>
          <Icon
            icon="ph:caret-down-bold"
            class="pointer-events-none absolute right-2 text-[18px] text-slate-400"
            aria-hidden="true"
          />
        </div>
        <button
          @click="createSession"
          class="text-sm bg-white dark_bg-gray-900 text-gray-700 dark_text-gray-200 rounded px-2 py-1 border border-gray-300 dark_border-gray-600 flex items-center gap-1 focus-visible_ring-4"
          aria-label="New chat session"
        >
          <Icon icon="ph:plus" class="text-xl" />
        </button>
        <button
          v-if="!isLiteMode"
          @click="exportSession"
          :disabled="!selectedSession"
          class="text-sm bg-white dark_bg-gray-900 text-gray-700 dark_text-gray-200 rounded px-2 py-1 border border-gray-300 dark_border-gray-600 flex items-center gap-1 disabled:opacity-50 focus-visible_ring-4"
          aria-label="Export session"
        >
          <Icon icon="mdi:download" class="text-xl" />
        </button>
        <button
          @click="documentsDialog.open"
          class="text-sm bg-white dark_bg-gray-900 text-gray-700 dark_text-gray-200 rounded px-2 py-1 border border-gray-300 dark_border-gray-600 flex items-center gap-1 focus-visible_ring-4"
          aria-label="Documents"
        >
          <Icon icon="mdi:tray-upload" class="text-xl" />
        </button>
        <!-- Edit pages toggle (visible only when YAML has top-level pages:) -->
        <label
          v-if="hasPagesTop"
          class="ms-2 inline-flex items-center gap-2 text-sm text-gray-700 dark_text-gray-200"
        >
          <input
            type="checkbox"
            v-model="editPages"
            class="accent-primary-600"
          />
          <span>Edit pages</span>
        </label>
        <button
          v-if="isLiteMode"
          @click="closeChatPane"
          class="ms-auto text-sm bg-white dark_bg-gray-900 text-gray-700 dark_text-gray-200 rounded px-2 py-1 flex items-center gap-1 focus-visible_ring-4"
          aria-label="Close chat pane"
        >
          <Icon icon="ph:arrow-line-left" class="text-xl" />
        </button>
      </div>
    </div>

    <DocumentsDialog
      :open="documentsDialog.isOpen"
      @close="documentsDialog.close"
      @use-document="title => agentRef?.attachDocument?.(title)"
    />

    <!-- Chat component (key forces remount on session switch) -->
    <!-- Make chat component flex-1 & overflow-hidden to constrain its internal layout -->
    <AuthorEditAgent
      ref="agentRef"
      class="flex-1 min-h-0 overflow-hidden"
      :key="selectedId"
      :courseId="courseId"
      :sessionId="selectedId"
      :firmCode="firmCode"
      :yaml="props.yaml"
      :currentScreen="props.currentScreen"
      :isLiteMode="props.isLiteMode"
      :creditMessage="creditMessage"
      :canSendWithCredits="canSendWithCredits"
      :refreshCredits="refreshCredits"
      :consumeCredit="consumeCredit"
      :initialMessages="initialMessages"
      :patchStates="patchStates"
      @view-patch="(pageId, screenId) => emit('view-patch', pageId, screenId)"
      @messages-updated="onMessagesUpdated"
      @patch-state="onPatchState"
      @open-documents-dialog="documentsDialog.open"
    />
  </div>
</template>

<script lang="ts" setup>
import {
  ref,
  onMounted,
  computed,
  toRaw,
  type MaybeRef,
  inject,
  provide,
  watch
} from 'vue';
import AuthorEditAgent from './AuthorEditAgent.vue';
import { EDITAGENT_API_BASE } from '../lib/editAgentApi';
import callClientPortal from '../../lib/callClientPortal';
import {
  listChats,
  saveChat,
  generateSessionId,
  APP_VERSION,
  type ChatSession,
  type ChatMessage
} from '../chat/chatStore';
import { normalizeMessage } from '../chat/normalizeMessage';
import { load as yamlLoad, dump as yamlDump } from 'js-yaml';
import { findScreenById } from '../../lib/applyPatch';
import { getMessageText, parseMessageContent } from '../lib/messageUtils';
import { Icon } from '@iconify/vue';
import useOpener from '../lib/useOpener';
import DocumentsDialog from './DocumentsDialog.vue';
import { useEditAgentCredits } from '../lib/useEditAgentCredits';

/* ------------------------------------------------------------------ */
/* Props / Emits                                                      */
/* ------------------------------------------------------------------ */

const props = defineProps<{
  courseId: number;
  firmCode?: string;
  yaml?: string;
  currentScreen?: MaybeRef<UcScreen>;
  isCustomisation?: boolean;
  hasBaseCourse?: boolean;
  isLiteMode?: boolean;
}>();

const emit = defineEmits<{
  (e: 'view-patch', pageId: string, screenId: string): void;
}>();

/* ------------------------------------------------------------------ */
/* Reactive state                                                     */
/* ------------------------------------------------------------------ */

const sessions = ref<ChatSession[]>([]);
const selectedId = ref<string>('');
const agentRef = ref<InstanceType<typeof AuthorEditAgent>>();
const editAgentCredits = useEditAgentCredits(!!props.isLiteMode);
const creditMessage = editAgentCredits.message;
const canSendWithCredits = editAgentCredits.canSend;
const refreshCredits = editAgentCredits.refresh;
const consumeCredit = editAgentCredits.consume;
const initializeEditAgentCredits = editAgentCredits.initialize;

const { courseId, firmCode } = props;
const closeChatPane = inject<() => void>('closeChatPane', () => {});

// YAML capability detection and local toggle
const hasPagesTop = computed(() => {
  // Courses with a base-course (e.g. duplicates) keep their pages in the base
  // course; the editor model only holds patches, so Base mode (upsertBase)
  // would fail with "missing top-level pages". Force patches. Standalone
  // bespoke courses have no base-course and their own pages, so Base is fine.
  if (props.isCustomisation || props.hasBaseCourse) return false;
  return !!props.yaml && /^\s*pages:\s*$/m.test(props.yaml);
});
const editPages = ref<boolean>(hasPagesTop.value);
watch(hasPagesTop, v => {
  editPages.value = v;
});

// Inject raw accept/revert functions from parent (AuthorEditor) and provide routed handler for child chat
const acceptBaseRaw = inject<(yaml: string) => void>('acceptBaseRaw');
const acceptPatchRaw = inject<(yaml: string) => void>('acceptPatchRaw');
const revertPatchRaw = inject<(screenId: string) => void>('revertPatchRaw');

provide(
  'acceptPatch',
  (screenYaml: string, messageId: string, editIndex: number) => {
    // Apply the patch using existing logic
    if (editPages.value) {
      acceptBaseRaw?.(screenYaml);
    } else {
      acceptPatchRaw?.(screenYaml);
    }
  }
);

provide(
  'revertPatch',
  (screenId: string, messageId: string, editIndex: number) => {
    const checkpoint = getCheckpointFromMessage(messageId, editIndex);

    if (checkpoint) {
      // Apply the checkpoint using the same method as accept
      if (editPages.value) {
        acceptBaseRaw?.(checkpoint);
      } else {
        acceptPatchRaw?.(checkpoint);
      }
    } else {
      // Fallback to old behavior if no checkpoint exists
      revertPatchRaw?.(screenId);
    }
  }
);

// Expose apply mode and acceptance hook to window for cross-panel integration
// @ts-ignore
(window as EditorWindow).getApplyMode = () =>
  editPages.value ? 'base' : 'patches';

// acceptActivePreview is exposed in AuthorEditor where previewRef is available

// The ChatSession currently selected (reactive)
const selectedSession = computed(() =>
  sessions.value.find(s => s.id === selectedId.value)
);

// Expose current session's patch states to child
const patchStates = computed<Record<string, 'accepted' | 'rejected'>>(() => {
  return (
    (selectedSession.value?.patchStates as Record<
      string,
      'accepted' | 'rejected'
    >) || {}
  );
});

// Transform stored messages into format expected by child component
const initialMessages = computed<ChatMessage[]>(() => {
  return (selectedSession.value?.messages ?? []).map(normalizeMessage);
});

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */

const formatOpt = (s: ChatSession) => `${s.title || 'New AI Chat'}`;

/** Persist given messages array to currently selected session */
async function persist(messages: ChatMessage[]) {
  const session = selectedSession.value;
  if (!session) return;

  session.messages = messages;
  // Leave title empty until AI supplies one
  session.updatedAt = Date.now();
  // Convert potentially reactive proxy to plain object before saving
  try {
    await saveChat(toRaw(session));
  } catch (err) {
    console.error('[persist] Failed to save messages to IndexedDB:', err);
    return;
  }

  /* -------------------------------------------------------------- */
  /* Parallel title generation using Cloudflare /api/chat-title      */
  /* -------------------------------------------------------------- */
  const hasUserMessage = messages.some(m => m.role === 'user');
  if (session.title === '' && hasUserMessage) {
    // Attempt generation if not done yet (allow retry by not using titleRequested)
    void generateChatTitle(session);
  }
}

/** Retrieve a checkpoint for a specific edit from the message metadata */
function getCheckpointFromMessage(
  messageId: string,
  editIndex: number
): string | null {
  const session = selectedSession.value;
  if (!session) return null;

  const message = session.messages.find(m => m.id === messageId);
  return message?.metadata?.checkpoints?.[editIndex] ?? null;
}

/** Create checkpoints for new edits in assistant messages that don't have them yet */
function createCheckpointsForNewEdits(messages: ChatMessage[]) {
  if (!props.yaml) return;

  for (const message of messages) {
    if (message.role !== 'assistant') continue;

    // Parse message content for edit blocks
    const text = getMessageText(message);
    const chunks = parseMessageContent(text);

    for (const chunk of chunks) {
      if (!chunk.complete) continue;
      if (chunk.type !== 'edit' && chunk.type !== 'text-edit') continue;

      const editIndex = chunk.index;

      // Skip if checkpoint already exists
      if (message.metadata?.checkpoints?.[editIndex]) continue;

      // Extract screen ID from edit content
      try {
        const parsed = yamlLoad(chunk.content) as any;
        const screenId = chunk.type === 'edit' ? parsed?.id : parsed?.screenId;
        if (!screenId || typeof screenId !== 'string') continue;

        // Find current screen state in compiled YAML
        const compiledObj = yamlLoad(props.yaml) as any;
        const currentScreen = findScreenById(compiledObj, screenId);
        if (!currentScreen) continue;

        // Save checkpoint to message metadata
        const checkpoint = yamlDump(currentScreen, { lineWidth: -1 });
        if (!message.metadata) message.metadata = {};
        if (!message.metadata.checkpoints) message.metadata.checkpoints = {};
        message.metadata.checkpoints[editIndex] = checkpoint;
      } catch (e) {
        console.warn('Failed to create checkpoint for edit', editIndex, e);
      }
    }
  }
}

/**
 * Generate a concise chat title via the Cloudflare edge function and persist
 * it back to IndexedDB. Runs once per session.
 */
async function generateChatTitle(session: ChatSession) {
  if (session.title && session.title !== '') return;
  try {
    const { response } = await callClientPortal(
      '/chat-title',
      {
        method: 'POST',
        body: JSON.stringify({ content: JSON.stringify(session.messages) })
      },
      { base: EDITAGENT_API_BASE }
    );

    // Edit-agent API returns { title: string } directly, not wrapped in portal format
    const data = response.data as { title?: string };
    if (data?.title && typeof data.title === 'string') {
      // Mutate reactive session object so dropdown updates immediately
      session.title = data.title.trim();
      session.updatedAt = Date.now();

      // Force Vue to recognise reactive change if object was raw-cloned elsewhere
      const idx = sessions.value.findIndex(s => s.id === session.id);
      if (idx !== -1) {
        sessions.value[idx] = {
          ...sessions.value[idx],
          title: session.title,
          updatedAt: session.updatedAt
        } as ChatSession;
      }

      // Persist updated title to IndexedDB
      await saveChat(toRaw(session));
    }
  } catch (err) {
    console.error('Failed to generate chat title', err);
  }
}

function onMessagesUpdated(msgs: any[]) {
  // Strip any Vue reactive proxies before normalizing so that nested objects
  // (e.g. metadata.checkpoints) are plain values that IndexedDB can clone.
  const mapped: ChatMessage[] = msgs.map(m => normalizeMessage(toRaw(m)));

  // Restore persisted checkpoints from session.messages into mapped.
  // The AI SDK does not carry our custom metadata.checkpoints on messages it
  // creates, so each onMessagesUpdated call would see them as missing and
  // recreate them from the current props.yaml — which may already include
  // accepted edits, corrupting the pre-edit snapshots.
  const sessionMsgs = selectedSession.value?.messages ?? [];
  for (const msg of mapped) {
    if (!msg.metadata?.checkpoints) {
      const saved = sessionMsgs.find(s => s.id === msg.id);
      if (saved?.metadata?.checkpoints) {
        if (!msg.metadata)
          msg.metadata = { createdAt: new Date().toISOString() };
        msg.metadata.checkpoints = { ...saved.metadata.checkpoints };
      }
    }
  }

  // Create checkpoints for any new edits that don't have them yet
  createCheckpointsForNewEdits(mapped);

  persist(mapped);
}

/* ------------------------------------------------------------------ */
/* Export current session                                             */
/* ------------------------------------------------------------------ */

function exportSession() {
  const session = selectedSession.value;
  if (!session) return;

  const data = JSON.stringify(toRaw(session), null, 2);
  const blob = new Blob([data], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const safeTitle = session.title
    ? session.title.replace(/[\\/:*?"<>|]/g, '_')
    : `chat_${session.id}`;

  const a = document.createElement('a');
  a.href = url;
  a.download = `${safeTitle}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/* ------------------------------------------------------------------ */
/* Patch state handling                                               */
/* ------------------------------------------------------------------ */

function onPatchState(
  messageId: string,
  editIndex: number,
  state: 'accepted' | 'rejected' | 'pending'
) {
  const session = selectedSession.value;
  if (!session) return;

  if (!session.patchStates) session.patchStates = {};

  const key = `${messageId}_${editIndex}`;
  if (state === 'pending') {
    // Clear persisted state
    if (key in session.patchStates) {
      delete (session.patchStates as Record<string, any>)[key];
    }
  } else {
    session.patchStates[key] = state;
  }
  session.updatedAt = Date.now();

  // Persist updated session (messages unchanged)
  void saveChat(toRaw(session));
}

/* ------------------------------------------------------------------ */
/* Session lifecycle                                                  */
/* ------------------------------------------------------------------ */

async function createSession() {
  const current = selectedSession.value;
  // Reuse existing blank session instead of creating duplicates
  if (current && (current.messages?.length ?? 0) === 0) {
    selectedId.value = current.id;
    return;
  }

  const now = Date.now();
  const id = generateSessionId(courseId, firmCode);
  const session: ChatSession = {
    id,
    courseId,
    firmCode,
    title: '',
    createdAt: now,
    updatedAt: now,
    version: APP_VERSION,
    messages: []
  };
  await saveChat(session);
  sessions.value.unshift(session);
  selectedId.value = id;
}

const documentsDialog = useOpener();

async function init() {
  const [savedSessions] = await Promise.all([
    listChats(courseId, firmCode),
    props.isLiteMode ? initializeEditAgentCredits() : Promise.resolve(null)
  ]);
  sessions.value = savedSessions;
  if (sessions.value.length) {
    selectedId.value = sessions.value[0].id;
  } else {
    await createSession();
  }
}

async function onSelect(e: Event) {
  const val = (e.target as HTMLSelectElement).value;
  // stop streaming before switching
  agentRef.value?.stop?.();
  selectedId.value = val;
}

onMounted(init);
</script>
